/**
 * The filters of an event subscription (TRUS-685): programs, assignees,
 * repositories and services. Which ones show depends on the event types on
 * offer (contract A: each type lists the filters that apply to it). Empty
 * means "all" for every filter.
 *
 * Programs and assignees reuse the org's existing pickers: the program list
 * (`GET /api/programs`) and the org staff list (`useOrgStaffOptions`), shown
 * in the shared StaffMultiSelect control (not portaled, so it works inside a
 * dialog). Personal subscriptions may also pick "Me" as an assignee.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { Input } from '@/components/ui/input';
import { StaffMultiSelect, type StaffMultiSelectOption } from '@/components/forms/StaffMultiSelect';
import { useOrgStaffOptions } from '@/components/forms/staffOptions';
import { programsAPI } from '@/services/api/programs';
import {
  SYSTEM_EVENT_REPOS,
  type AssigneeFilter,
  type SystemEventFilterKind,
  type SystemEventFilters,
} from '@/services/systemEventsApi';
import { parseServices, repoLabel, toggleValue, type TFn } from './eventCatalog';

export const eventFilterProgramsKey = ['system-event-filter-programs'] as const;

/** The org's programs and staff as picker options (shared with read-only summaries). */
export function useEventFilterOptions(enabled = true): {
  programs: StaffMultiSelectOption[];
  staff: StaffMultiSelectOption[];
} {
  const programsQuery = useQuery({
    queryKey: eventFilterProgramsKey,
    queryFn: async () => {
      const rows = await programsAPI.listPrograms();
      return (Array.isArray(rows) ? rows : [])
        .filter((p) => p && p.is_active !== false && Number(p.id) > 0)
        .map((p) => ({ label: String(p.name || p.id), value: String(p.id) }));
    },
    enabled,
    staleTime: 5 * 60_000,
  });
  const staff = useOrgStaffOptions({ includeOrgAdmins: true });
  return { programs: programsQuery.data ?? [], staff };
}

/** "Program A, Program B" / "Me, Alice" for a read-only summary. */
export function describeFilterIds(
  values: Array<number | string>,
  options: StaffMultiSelectOption[],
  t: TFn,
): string {
  return values
    .map((v) =>
      v === 'me'
        ? t('messaging.systemEvents.filters.me', 'Me')
        : options.find((o) => o.value === String(v))?.label ?? `#${v}`,
    )
    .join(', ');
}

interface EventFilterFieldsProps {
  kinds: Set<SystemEventFilterKind>;
  value: SystemEventFilters;
  onChange: (next: SystemEventFilters) => void;
  disabled?: boolean;
  /** Offer "Me" in the assignees picker (personal subscriptions only). */
  allowMe?: boolean;
  /** Prefix for element ids, unique per mounted form. */
  idPrefix: string;
}

export function EventFilterFields({ kinds, value, onChange, disabled, allowMe, idPrefix }: EventFilterFieldsProps) {
  const { t } = useAppTranslation();
  const { programs, staff } = useEventFilterOptions(kinds.has('programs'));
  const [servicesText, setServicesText] = React.useState(value.services.join(', '));

  // Re-sync the free-text box when the saved value is (re)loaded.
  const servicesKey = value.services.join(',');
  React.useEffect(() => {
    if (parseServices(servicesText).join(',') !== servicesKey) setServicesText(value.services.join(', '));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servicesKey]);

  const assigneeOptions = React.useMemo(() => {
    const sorted = [...staff].sort((a, b) => a.label.localeCompare(b.label));
    return allowMe ? [{ label: t('messaging.systemEvents.filters.me', 'Me'), value: 'me' }, ...sorted] : sorted;
  }, [staff, allowMe, t]);

  const pickerText = {
    searchPlaceholder: t('messaging.systemEvents.filters.search', 'Search…'),
    clearLabel: t('messaging.systemEvents.filters.clear', 'Clear all'),
    noMatchLabel: (q: string) => t('messaging.systemEvents.filters.noMatch', 'No match for “{{query}}”', { query: q }),
    removeLabel: (label: string) => t('messaging.systemEvents.filters.remove', 'Remove {{name}}', { name: label }),
  };

  if (kinds.size === 0) return null;

  return (
    <div className="space-y-4" data-testid="event-filter-fields">
      {kinds.has('programs') && (
        <div className="space-y-1.5" data-testid="event-filter-programs">
          <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('messaging.systemEvents.filters.programs', 'Programs')}
          </span>
          <StaffMultiSelect
            options={programs}
            value={value.programs.map(String)}
            onChange={(next) => onChange({ ...value, programs: next.map(Number).filter((n) => n > 0) })}
            disabled={disabled}
            ariaLabel={t('messaging.systemEvents.filters.programs', 'Programs')}
            placeholder={t('messaging.systemEvents.filters.allPrograms', 'All programs')}
            emptyHint={t('messaging.systemEvents.filters.noPrograms', 'No programs found')}
            {...pickerText}
          />
        </div>
      )}

      {kinds.has('assignees') && (
        <div className="space-y-1.5" data-testid="event-filter-assignees">
          <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('messaging.systemEvents.filters.assignees', 'Assigned to')}
          </span>
          <StaffMultiSelect
            options={assigneeOptions}
            value={value.assignees.map(String)}
            onChange={(next) =>
              onChange({
                ...value,
                assignees: next
                  .map((v): AssigneeFilter | null => (v === 'me' ? (allowMe ? 'me' : null) : Number(v) > 0 ? Number(v) : null))
                  .filter((v): v is AssigneeFilter => v !== null),
              })
            }
            disabled={disabled}
            sort={false}
            ariaLabel={t('messaging.systemEvents.filters.assignees', 'Assigned to')}
            placeholder={t('messaging.systemEvents.filters.anyone', 'Anyone')}
            emptyHint={t('messaging.systemEvents.filters.noStaff', 'No staff found')}
            {...pickerText}
          />
        </div>
      )}

      {kinds.has('repos') && (
        <fieldset className="space-y-1.5">
          <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('messaging.systemEvents.reposLegend', 'Repositories (code events)')}
          </legend>
          <div className="flex flex-wrap gap-4">
            {SYSTEM_EVENT_REPOS.map((repo) => (
              <label key={repo} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[var(--primary,#2563EB)]"
                  checked={value.repos.includes(repo)}
                  onChange={(e) => onChange({ ...value, repos: toggleValue(value.repos, repo, e.target.checked) })}
                  disabled={disabled}
                  data-testid={`event-repo-${repo}`}
                />
                {repoLabel(repo, t)}
              </label>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            {t('messaging.systemEvents.reposHint', 'None selected means every repository.')}
          </p>
        </fieldset>
      )}

      {kinds.has('services') && (
        <div className="space-y-1.5">
          <label
            htmlFor={`${idPrefix}-services`}
            className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
          >
            {t('messaging.systemEvents.servicesLabel', 'Services (deploy events)')}
          </label>
          <Input
            id={`${idPrefix}-services`}
            value={servicesText}
            onChange={(e) => {
              setServicesText(e.target.value);
              onChange({ ...value, services: parseServices(e.target.value) });
            }}
            placeholder={t('messaging.systemEvents.servicesPlaceholder', 'e.g. weavein-api, celery-worker')}
            disabled={disabled}
            data-testid="event-services"
          />
          <p className="text-xs text-muted-foreground">
            {t('messaging.systemEvents.servicesHint', 'Separate with commas. Leave empty for every service.')}
          </p>
        </div>
      )}
    </div>
  );
}

export default EventFilterFields;
